// ── Welle A3: feste Liste der Aktionen in Beiträgen (Schritte im Ernstfall, Sicherheits-Check) ──
// Nur diese vier internen Aktionen; Beiträge können keine Skripte oder fremden Links auslösen.
import React, { useState } from 'react';
import { API_BASE, apiGet, apiHeaders, apiPost, zeigeApiFehler } from '../../lib/api';
import { confirm } from '../../lib/confirm';
import { toast } from '../../lib/toast';
import { abmeldenUndAufraeumen, ersetzeToken } from '../../lib/sitzung';
import { LdSheet } from '../../components/ui/LdSheet';
import { LdEingabe, LdFeld, LdFensterFuss } from '../../components/ld/LdFenster';
import type { Aktion } from './typen';

export async function ueberallAbmelden() {
  const ok = await confirm('Alle Geräte, auf denen du angemeldet bist, werden abgemeldet, auch dieses. Danach meldest du dich neu an.',
    { title: 'Auf allen Geräten abmelden?', confirmLabel: 'Überall abmelden' });
  if (!ok) return;
  try { await apiPost('/api/auth/logout-all-devices'); } catch (e) { zeigeApiFehler(e, 'Abmelden fehlgeschlagen'); return; }
  void abmeldenUndAufraeumen();
}

export async function ladeDatenexport() {
  try {
    const r = await fetch(`${API_BASE}/api/datenschutz/export`, { headers: apiHeaders() });
    if (!r.ok) { toast('Export fehlgeschlagen', 'error'); return false; }
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement('a');
    a.href = url; a.download = `lehrerdesk-export-${new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' })}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast('Datenexport heruntergeladen');
    return true;
  } catch { toast('Export fehlgeschlagen', 'error'); return false; }
}

/** Hält die Fenster für „Passwort ändern“ und „Meine Aktivität“; ausfuehren(aktion) startet eine Aktion */
export function useAktionen(nachExport?: () => void) {
  const [fenster, setFenster] = useState<'passwort' | 'aktivitaet' | null>(null);
  async function ausfuehren(a: Aktion) {
    if (a === 'ueberall_abmelden') return ueberallAbmelden();
    if (a === 'passwort_aendern') return setFenster('passwort');
    if (a === 'meine_aktivitaet') return setFenster('aktivitaet');
    if (a === 'datenexport' && await ladeDatenexport()) nachExport?.();
  }
  const fensterEl = (
    <>
      <PasswortFenster open={fenster === 'passwort'} onClose={() => setFenster(null)} />
      <AktivitaetFenster open={fenster === 'aktivitaet'} onClose={() => setFenster(null)} />
    </>
  );
  return { ausfuehren, fensterEl };
}

export function PasswortFenster({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [alt, setAlt] = useState('');
  const [neu, setNeu] = useState('');
  const [neu2, setNeu2] = useState('');
  const [laden, setLaden] = useState(false);
  const fehler = neu && neu.length < 8 ? 'Mindestens 8 Zeichen.' : neu2 && neu !== neu2 ? 'Die beiden Eingaben sind verschieden.' : null;
  function zu() { setAlt(''); setNeu(''); setNeu2(''); onClose(); }
  async function speichern() {
    setLaden(true);
    try {
      const r = await apiPost<{ token: string }>('/api/auth/passwort', { altes_passwort: alt, neues_passwort: neu });
      ersetzeToken(r.token);
      toast('Passwort geändert. Andere Geräte sind abgemeldet.');
      zu();
    } catch (e) { zeigeApiFehler(e, 'Passwort ändern fehlgeschlagen'); } finally { setLaden(false); }
  }
  return (
    <LdSheet open={open} onClose={zu} maxWidth={480} kopf={{ titel: 'Passwort ändern', kontext: 'Danach sind alle anderen Geräte abgemeldet.', icon: 'key' }}
      fuss={<LdFensterFuss onAbbrechen={zu} haupt="Passwort ändern" onHaupt={speichern} aus={!alt || neu.length < 8 || neu !== neu2} laden={laden} />}>
      <LdFeld label="Bisheriges Passwort" htmlFor="pw-alt">
        <LdEingabe id="pw-alt" type="password" autoComplete="current-password" value={alt} onChange={e => setAlt(e.target.value)} />
      </LdFeld>
      <LdFeld label="Neues Passwort" htmlFor="pw-neu" hinweis="Ein neues, das du nirgends sonst benutzt.">
        <LdEingabe id="pw-neu" type="password" autoComplete="new-password" value={neu} onChange={e => setNeu(e.target.value)} />
      </LdFeld>
      <LdFeld label="Neues Passwort wiederholen" htmlFor="pw-neu2" fehler={fehler ?? undefined}>
        <LdEingabe id="pw-neu2" type="password" autoComplete="new-password" value={neu2} onChange={e => setNeu2(e.target.value)} />
      </LdFeld>
    </LdSheet>
  );
}

type AuditEintrag = { id: number; entity_type: string; entity_id: number | null; action: string; created_at: string };
const WAS: Record<string, string> = {
  note: 'Note', fehlzeit: 'Fehlzeit', schueler: 'Schüler', gespraech: 'Gespräch', mitarbeit: 'Mündliche Mitarbeit',
  halbjahres_note: 'Halbjahresnote', vorjahres_note: 'Vorjahresnote', mdl_vorschlag: 'Notenvorschlag', schultermin: 'Termin',
  alle_sitzungen: 'Auf allen Geräten abgemeldet', passwort: 'Passwort geändert',
};
const TAT: Record<string, string> = { create: 'erstellt', update: 'geändert', delete: 'gelöscht', verlassen: 'als verlassen markiert', reaktivieren: 'reaktiviert' };

export function AktivitaetFenster({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [liste, setListe] = useState<AuditEintrag[] | null>(null);
  React.useEffect(() => {
    if (!open) return;
    setListe(null);
    apiGet<AuditEintrag[]>('/api/audit/eigene').then(setListe).catch(e => { zeigeApiFehler(e, 'Aktivität laden fehlgeschlagen'); setListe([]); });
  }, [open]);
  return (
    <LdSheet open={open} onClose={onClose} maxWidth={560} kopf={{ titel: 'Meine Aktivität', kontext: 'Die letzten 50 Einträge. Siehst du etwas, das nicht von dir ist?', icon: 'history' }}>
      {liste == null ? <p className="ld-si-leise">Wird geladen …</p> : liste.length === 0 ? <p className="ld-si-leise">Keine Aktivitäten vorhanden.</p> : (
        <ul className="ld-si-akt">
          {liste.map(e => {
            const ganz = e.entity_type === 'alle_sitzungen' || e.entity_type === 'passwort';
            return (
              <li key={e.id}>
                <b>{ganz ? WAS[e.entity_type] : `${WAS[e.entity_type] ?? e.entity_type} ${TAT[e.action] ?? e.action}`}</b>
                <small>{new Date(e.created_at.replace(' ', 'T') + (e.created_at.includes('Z') ? '' : 'Z')).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</small>
              </li>
            );
          })}
        </ul>
      )}
    </LdSheet>
  );
}
